/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';
import ROUTES from '@src/constants/route';
import {
  Box,
  Button,
  List,
  ListItem,
  // ListItem,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Check, Clear } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  DURATION,
  FREE_PACKAGE_CODES,
  PACKAGE_CODE,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
} from '@src/constants/package';
import actions from '@src/redux/actions';
import i18n from '@src/languages';
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded';
import apis from '@src/apis';
import { trackFreePlanActivated } from '@src/services/moengage';
import ProcessHandler from '@src/components/ProcessHandler';
import { LANGUAGE } from '@src/constants';
import { ORDER_STATUS } from '@src/constants/order';
import NotificationDialog from '@src/components/NotificationDialog';
import { checkVietNam } from '@src/utils/checkCountry';
import { delay } from '@src/utils/delay';
import { getPackageCurrentPrice } from '@src/services/package';
// import PopularIcon from '@src/assets/icons/popular.svg';
import Tooltip from '@src/components/Tooltip';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import useHandleOrderError from '@src/hooks/useHandleOrderError';
import { StyledNewPackageItem, StyledPackageItemWrapper } from './index.style';
import AmountCredits from './AmountCredits';

const FreePackageRow = ({ pkg, duration }) => {
  const isDubbingRoute = useCheckDubbingRoute();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { handleOrderError } = useHandleOrderError();
  const { user = {} } = useSelector((state) => state.auth);
  const { usingPackage, latestOrder, usingDubbingPackage, latestDubbingOrder } =
    useSelector((state) => state.user);
  const userRef = useRef(user);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState({
    createFreeOrder: false,
    cancelOrder: false,
  });
  const [packageContents, setPackageContents] = useState([]);

  const isVietNam = checkVietNam();

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const isExpiredPackage = checkExpiredDate(user?.packageExpiryDate);

  const { language } = i18n;
  const { price, usdPrice } = getPackageCurrentPrice(pkg, true);
  const isUsingFreePackage =
    [PACKAGE_LEVEL.BASIC, PACKAGE_LEVEL.DUBBING_BASIC].includes(pkg.level) &&
    ((!isDubbingRoute && usingPackage.code === pkg.code) ||
      (isDubbingRoute && usingDubbingPackage?.code === pkg.code));

  const handleCreateFreeOrder = async (packageId) => {
    if (!isDubbingRoute && usingPackage.level === PACKAGE_LEVEL.BASIC) return;

    if (
      isDubbingRoute &&
      usingDubbingPackage?.level === PACKAGE_LEVEL.DUBBING_BASIC
    )
      return;

    if (latestOrder.status === ORDER_STATUS.PENDING) {
      setOpenDialog(true);
      return;
    }

    setLoading({ ...loading, createFreeOrder: true });
    const orderData = await apis.orders.createOrder({
      packageId,
      isGlobal: !isVietNam,
      downgradePackage: true,
    });
    setLoading({ ...loading, createFreeOrder: false });

    if (!orderData?.status) {
      // Check for error code 6001 and show popup
      if (handleOrderError(orderData)) {
        return;
      }
    }

    if (orderData.status) {
      trackFreePlanActivated({
        previousPackageCode: usingPackage?.code,
      });
      if (isDubbingRoute)
        dispatch(actions.user.getLatestDubbingOrderSuccess(orderData.result));
      else dispatch(actions.user.getLatestOrderSuccess(orderData.result));
      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'buyPackageSuccessfully',
        }),
      );
      if (orderData.result.type === PACKAGE_TYPE.DUBBING) {
        dispatch(
          actions.user.getUsingDubbingPackageSuccess(orderData.result.package),
        );
        dispatch(
          actions.auth.updateUserInfo({
            ...userRef.current,
            dubbing: {
              ...userRef.current.dubbing,
              packageCode: orderData.result?.package?.code,
              packageExpiryDate: orderData.result?.packageExpiryDate,
            },
          }),
        );
        history.push(ROUTES.HOME_DUBBING);
      } else {
        dispatch(actions.user.getUsingPackageSuccess(orderData.result.package));
        history.push(ROUTES.TTS);
      }
      return;
    }

    dispatch(
      actions.noti.push({ severity: 'error', message: 'buyPackageFailure' }),
    );
  };

  const handleBuyPackage = async () => {
    if (pkg.price === 0) {
      if (isUsingFreePackage) return;
      await delay(1000);
      handleCreateFreeOrder(pkg.id);
    }
  };

  const handleCancelPendingOrder = async () => {
    if (!isDubbingRoute && latestOrder.status !== ORDER_STATUS.PENDING) return;
    if (isDubbingRoute && latestDubbingOrder.status !== ORDER_STATUS.PENDING)
      return;

    setLoading({ ...loading, cancelOrder: true });
    const orderData = await apis.orders.cancelOrder(latestOrder.id);
    setLoading({ ...loading, cancelOrder: false });
    if (!orderData.status)
      dispatch(actions.noti.push({ severity: 'error', code: orderData.code }));
    else {
      if (pkg.result.type === PACKAGE_TYPE.STUDIO)
        dispatch(actions.user.getLatestOrderSuccess(pkg.result));
      else if (pkg.result.type === PACKAGE_TYPE.DUBBING)
        dispatch(actions.user.getLatestDubbingOrderSuccess(pkg.result));

      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'cancelOrderSuccessfully',
        }),
      );
    }

    setOpenDialog(false);
    setLoading({ ...loading, createFreeOrder: false });
  };

  const handleContinuePayingDialog = () => {
    if (latestOrder.status !== ORDER_STATUS.PENDING) return;
    if (latestOrder.paymentLink)
      window.location.assign(latestOrder.paymentLink);
    else history.replace({ search: `?package=${pkg.code}` });
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const renderButtonTextV3 = () =>
    FREE_PACKAGE_CODES?.includes(usingPackage?.code)
      ? t('using')
      : t('activeNow');

  const getPackageContents = ({
    contents,
    maxSeconds,
    packageType,
    packageDuration,
  }) => {
    const isFreePackageV3 = pkg?.code === PACKAGE_CODE.STUDIO_FREE_V3;
    if (packageDuration === DURATION.MONTHLY && isFreePackageV3)
      return contents.filter(
        (item) => !item?.vi?.includes('Không có giọng nhân bản'),
      );
    if (packageType !== PACKAGE_TYPE.DUBBING) return contents;

    // Calculate unit price / minute
    let unitPrice;
    if (isVietNam) unitPrice = Math.round((price / maxSeconds) * 60);
    else {
      unitPrice = (usdPrice / maxSeconds) * 60;
      unitPrice = Math.round(unitPrice * 100) / 100;
    }

    // Create unit price content
    const viUnitPriceContent = {
      vi: `Đơn giá <br><b>₫${unitPrice.toLocaleString(language)} / phút</b>`,
      en: `Unit price <br><b>₫${unitPrice.toLocaleString(
        language,
      )} / minute</b>`,
      active: true,
    };

    const usdUnitPriceContent = {
      vi: `Đơn giá <br><b>$${unitPrice.toLocaleString(language)} / phút</b>`,
      en: `Unit price <br><b>$${unitPrice.toLocaleString(
        language,
      )} / minute</b>`,
      active: true,
    };

    const unitPriceContent = isVietNam
      ? viUnitPriceContent
      : usdUnitPriceContent;
    const newContents = [...contents];
    newContents.splice(1, 0, unitPriceContent);

    return newContents;
  };

  const checkDisplayBuyButton = (studioPackages) => {
    const basicTrialStudioPackages = [PACKAGE_LEVEL.TRIAL];

    return (
      pkg.price > 0 ||
      !studioPackages?.id ||
      basicTrialStudioPackages.includes(studioPackages?.level)
    );
  };
  const displayBuyButton = checkDisplayBuyButton(usingPackage);

  const renderPrice = (priceValue) => {
    let formattedPrice = priceValue?.toLocaleString(language);

    if (!isVietNam && formattedPrice !== '0') {
      const number = +Number(formattedPrice.replace(',', '.'));
      formattedPrice = number.toLocaleString(language, {
        minimumFractionDigits: 2,
      });
    }

    const renderInternationPrice = () => {
      if (pkg.code === PACKAGE_CODE.API_ENTERPRISE_YEAR_V2)
        return (
          <span className="three-first">
            {/* Case have code === Enterprise API */}
            {priceValue?.toLocaleString(language)}
          </span>
        );
      if (isVietNam)
        return (
          <>
            <span className="three-first">
              {/* Format before 4 last character is big */}
              {formattedPrice.slice(0, -4) || '0'}
            </span>
            {/* Format last 4 character is small */}
            <span className="three-last">
              {formattedPrice.slice(-4) === '0' ? '' : formattedPrice.slice(-4)}
            </span>
          </>
        );
      return formattedPrice;
    };

    return (
      <>
        <Typography
          className={classnames(
            'price',
            {
              'price-en': !isVietNam && priceValue > 0,
            },
            {
              'unit-price-vip': pkg?.level === PACKAGE_LEVEL.VIP,
            },
          )}
        >
          <span className={`${'unit-price'} ${isVietNam ? 'underlined' : ''}`}>
            {isVietNam ? 'đ' : '$'}
          </span>
          {/* PSUGO pakage have month price < 1 */}
          {renderInternationPrice()}
        </Typography>
      </>
    );
  };

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (pkg?.contents) {
      const contents = getPackageContents({
        contents: pkg.contents,
        maxSeconds: pkg.maxSeconds,
        packageType: pkg.type,
        packageDuration: duration,
      });
      setPackageContents(contents);
    }
  }, [pkg?.contents, language, isVietNam, duration]);

  const PackageHeader = () => (
    <>
      <div className="package-header">
        <div className="name-content">
          <Typography
            className={classnames('package-name', {
              'package-name-row': true,
            })}
          >
            {pkg.name && (pkg.name[language] || pkg.name[LANGUAGE.EN])}
          </Typography>
        </div>
      </div>
      {pkg?.type !== PACKAGE_TYPE.API && (
        <Typography
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(
              language === LANGUAGE.VN
                ? pkg?.description?.[language]
                : pkg?.description?.[LANGUAGE.EN],
            ),
          }}
          className="description-package"
        />
      )}
    </>
  );
  const PackagePrice = () => (
    <>
      <div className="price-wrapper">
        {/* Monthly price with sale off */}
        <div
          className={classnames('price-package', {
            'price-package-vip': pkg?.level === PACKAGE_LEVEL.VIP,
          })}
        >
          {isVietNam ? renderPrice(price) : renderPrice(usdPrice)}
        </div>
      </div>
    </>
  );

  const buttonBuyPackageView = () => (
    <Button
      fullWidth
      className={classnames('button-start')}
      variant="outlined"
      data-id="add-to-cart"
      data-product={pkg?.type}
      data-package-code={pkg?.code}
      data-pricing-duration={duration}
      data-button-text={renderButtonTextV3()}
      onClick={() => handleBuyPackage(null)}
      disabled={!displayBuyButton && !isExpiredPackage}
    >
      {renderButtonTextV3()}
    </Button>
  );

  return (
    <>
      <StyledPackageItemWrapper isPoppular={pkg.mostPopular} isRow>
        <StyledNewPackageItem
          isVietNam={isVietNam}
          isRow
          isDisableButton={!displayBuyButton && !isExpiredPackage}
        >
          <div className="package-free-row">
            {/** Package Header */}
            <div className="package-free-header">
              <PackageHeader />
              <List className="list-package-content">
                <AmountCredits pkg={pkg} />
                {packageContents?.map((item) => (
                  <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="space-between"
                  >
                    <div className="clarify package-content-item" key={item.vi}>
                      <div className="flexItem">
                        <div className="icon-contents">
                          {item.active ? (
                            <FiberManualRecordRoundedIcon className="dots" />
                          ) : (
                            <Clear fontSize="small" color="light" />
                          )}
                        </div>
                        <div
                          className={
                            item.active ? 'content' : 'content-deactivate'
                          }
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html:
                              language === LANGUAGE.VN
                                ? item?.[language]
                                    .replaceAll(',', '.')
                                    .replaceAll(
                                      '<b>',
                                      `<b style='white-space:nowrap'>`,
                                    )
                                : item[LANGUAGE.EN],
                          }}
                        />
                      </div>
                      <div className="tooltip">
                        {item?.tooltip && (
                          <Tooltip
                            arrow
                            className="tootlip"
                            title={item?.tooltip?.[language] || ''}
                            placement="left"
                          >
                            <InfoOutlinedIcon />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    {[PACKAGE_LEVEL.BASIC, PACKAGE_LEVEL.TRIAL].includes(
                      pkg.level,
                    ) &&
                      item.en.includes('Voices') && (
                        <Box paddingTop="8px">
                          <Tooltip
                            arrow
                            className="tootlip"
                            title={t('internationalVoicesNotSupport')}
                            placement="left"
                          >
                            <InfoOutlinedIcon />
                          </Tooltip>
                        </Box>
                      )}
                  </Box>
                ))}
              </List>
            </div>

            {/** Button buy package */}
            <div className="card-content-free">
              {!!pkg?.featuresTitle && (
                <Typography
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      language === LANGUAGE.VN
                        ? pkg?.featuresTitle?.[language]
                        : pkg?.featuresTitle?.[LANGUAGE.EN],
                    ),
                  }}
                  className="title-text"
                />
              )}
              {pkg?.featuresDescription?.length > 0 && (
                <List className="list-package-content-free">
                  {pkg?.featuresDescription?.map((item) => (
                    <Box display="flex" justifyContent="space-between">
                      <ListItem className="clarify" key={item.vi}>
                        <Box style={{ width: '100%' }} className="flexItem">
                          <div className="flexItem">
                            <div className="icon-contents">
                              <Check
                                fontSize="small"
                                className={
                                  item?.advanced
                                    ? 'check-icon coming-soon-text-advanced'
                                    : 'check-icon'
                                }
                              />
                            </div>
                            <span className="item-package">
                              <div
                                className={
                                  item?.advanced
                                    ? 'coming-soon-text-advanced content'
                                    : 'coming-soon-text content'
                                }
                              >
                                {language === LANGUAGE.VN
                                  ? item[language]
                                  : item[LANGUAGE.EN]}
                                {item?.comingSoon && (
                                  <span className="coming-soon-title">
                                    ({t('comingSoon')})
                                  </span>
                                )}
                              </div>
                            </span>
                          </div>
                          <div className="tooltip">
                            {item?.tooltip && (
                              <Tooltip
                                arrow
                                title={item?.tooltip?.[language] || ''}
                                placement="left"
                                className="tootlip"
                              >
                                <InfoOutlinedIcon />
                              </Tooltip>
                            )}
                          </div>
                        </Box>
                      </ListItem>
                    </Box>
                  ))}
                </List>
              )}
            </div>
            {/** Package Price */}
            <div className="price-free-row">
              <PackagePrice />
              {buttonBuyPackageView()}
            </div>
          </div>

          {pkg.price === 0 && (
            <NotificationDialog
              name="pendingOrder"
              title={t('havePendingOrderTitle')}
              description={t('hasOrderWaitForConfirmTransferDescription')}
              variant="warning"
              open={openDialog}
              onClose={handleCloseDialog}
              actionComponent={
                <>
                  <Button variant="outlined" onClick={handleCancelPendingOrder}>
                    <ProcessHandler loading={loading.cancelOrder} size={25}>
                      {t('cancelOrder')}
                    </ProcessHandler>
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleContinuePayingDialog}
                  >
                    {t('continue')}
                  </Button>
                </>
              }
            />
          )}
        </StyledNewPackageItem>
      </StyledPackageItemWrapper>
    </>
  );
};

export default FreePackageRow;
